import { LANGUAGES, type LanguageCode } from './i18n';
import type { AppConfig, ChatMessage, Route } from './types';

export const APP_VERSION = '25.0';
export const SESSION_MS = 5 * 60 * 1000;
export const IDLE_MS = 3 * 60 * 1000;
export const DEVICE_KEY = 'talkxiDevice';
export const INSTALL_KEY = 'talkxiInstallId';

function storedLang(): LanguageCode {
  const saved = localStorage.getItem('t2alang');
  return saved && saved in LANGUAGES ? (saved as LanguageCode) : 'es';
}

function storedRoute(): Route {
  try {
    const parsed = JSON.parse(localStorage.getItem('t2aroute') ?? 'null');
    if (parsed && typeof parsed.from === 'string' && typeof parsed.to === 'string') return parsed as Route;
  } catch { /* valor corrupto: se ignora */ }
  return { key: 'configured', from: '', to: '' };
}

export const config: AppConfig = {
  brandName: 'Talkxi', tenantId: 'unbound', tenantName: 'Talkxi', vehicleId: 'unbound',
  deploymentId: 'unbound', publicDomain: 'talkxi.es', appUrl: 'https://talkxi.es/',
  website: '', privacyUrl: './privacidad-ai.html', whatsapp: '', googleReviewUrl: '',
  googleReviewQr: '', serviceArea: '', defaultRouteFrom: '', defaultRouteTo: '',
  maxPassengers: 6, features: {},
};

export const state = {
  lang: storedLang(),
  history: [] as ChatMessage[],
  route: storedRoute(),
  lastActivity: Date.now(),
  lastTouch: Date.now(),
  idleResetDone: false,
  aiNoticeSeen: false,
  sessionTracked: false,
  pendingAfterNotice: null as (() => void) | null,
  startupAction: new URLSearchParams(location.search).get('open') === 'chat' ? 'chat' : null,
  autoVoice: localStorage.getItem('t2aAutoVoice') !== '0',
  webSearch: localStorage.getItem('t2aWebSearch') !== '0',
  voiceVolume: Math.max(0, Math.min(1, Number(localStorage.getItem('t2aVoiceVolume') ?? '1'))),
  wakeLock: null as WakeLockSentinel | null,
};

export const L = () => LANGUAGES[state.lang];

export function setLanguage(code: LanguageCode): void {
  state.lang = code;
  localStorage.setItem('t2alang', code);
}

export function saveRoute(route: Route): void {
  state.route = route;
  localStorage.setItem('t2aroute', JSON.stringify(route));
  localStorage.setItem('talkxiRouteScope', `${config.tenantId}|${config.vehicleId}`);
}

export function resetHistory(): void {
  state.history = [];
  state.lastActivity = Date.now();
}

export function touchSession(): void {
  state.lastActivity = Date.now();
}

export function expireSession(): void {
  if (state.history.length && Date.now() - state.lastActivity > SESSION_MS) resetHistory();
}

export function installId(): string {
  const existing = localStorage.getItem(INSTALL_KEY) ?? '';
  if (/^[a-z0-9_-]{12,100}$/.test(existing)) return existing;
  const raw = crypto.randomUUID?.() ?? `tx-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const id = `tablet-${raw}`.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 100);
  localStorage.setItem(INSTALL_KEY, id);
  return id;
}
